import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import useAutosizeTextArea from "../../../../../../hooks/useAutosizeTextArea";
import ConfirmationModal from "../../../../../../parts/modals/ConfirmationModal";
import SvgIcon from "../../../../../../parts/SvgIcon";
import {
  MAX_WORKOUT_NOTE_LENGTH,
  SECTION_TYPES,
  WORKOUT_NOTE_ORDER,
} from "../../../../../../utils/section-types";

/*
 * Workout-day instructions: a single free-text block pinned to the top of a
 * workout day/session (a "workout-note" typed exercise-section — see
 * js/utils/section-types.js). Unlike SectionSeparator, it is never
 * draggable and never carries `.section-separator`/`data-section-id`/
 * `data-order` — that alone keeps it out of every drag/reorder selector in
 * DayEditor.js and EditorSessionTable.js, so nothing can ever land above it.
 *
 * Always renders read only, even when `editable` (entering calendar edit
 * mode doesn't open it). Clicking the note while `editable` switches it into
 * an inline edit mode (coach-notes styling) with Save/Delete; Save persists
 * and returns to read only, Delete opens the removal confirmation modal.
 *
 * `note` is the exercise-section doc, or null/undefined when the day/session
 * has none — this component renders nothing in that case, unless `creating`
 * is set: then it opens straight into edit mode for a brand-new note
 * (Program Builder's "+ Instructions" header pill, Calendar Day view's
 * "+ Instructions" button), and Save creates the exercise-section with the
 * scope fields in `createBody` (`utcDate`/`date` for a calendar day, or
 * `cycleId`/`program`/`programWeek`/`programSession` for a program session,
 * exactly what /actions/exercise-section/create already accepts). The delete
 * button just discards the draft in that case (`onCancelCreate`).
 */
const WorkoutNoteBlock = ({
  note,
  athleteId,
  reqSource = "calendar",
  // The organization owner's id — set when the acting coach is a subcoach
  // editing a program template, mirrors SectionSeparator.js.
  organizationCoach,
  editable = true,
  // Calendar month view (non-edit mode): renders the title inside the card
  // with a leading chevron, collapsed by default, instead of the label
  // sitting above the card.
  collapsible = false,
  // Create flow (see above): no `note` yet, open in edit mode.
  creating = false,
  createBody,
  onCancelCreate,
}) => {
  const { t } = useTranslation(["athlete-portal", "common"]);
  const [editing, setEditing] = useState(creating);
  const [menuOpened, setMenuOpened] = useState(false);
  const [description, setDescription] = useState(note?.description || "");
  const [showDelete, setShowDelete] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const textAreaRef = useRef(null);

  useAutosizeTextArea(textAreaRef.current, description);

  useEffect(() => {
    if (creating) setEditing(true);
  }, [creating]);

  useEffect(() => {
    if (editing) textAreaRef.current?.focus({ preventScroll: true });
  }, [editing]);

  const isNew = !note && creating;
  if (!note && !isNew) return null;

  const query = `?proxyAthlete=${athleteId}&reqSource=${reqSource}${
    organizationCoach ? `&organizationCoach=${organizationCoach}` : ""
  }`;

  const saveDescription = () => {
    const trimmed = (description || "").trim();
    if (isNew) {
      if (!trimmed) return;
      setEditing(false);
      submit(`/actions/exercise-section/create${query}`, {
        method: "POST",
        body: {
          ...createBody,
          type: SECTION_TYPES.WORKOUT_NOTE,
          description: trimmed,
          order: WORKOUT_NOTE_ORDER,
        },
      });
      onCancelCreate?.();
      return;
    }
    setEditing(false);
    if (trimmed === (note.description || "").trim()) return;
    submit(`/actions/exercise-section/update/${note._id}${query}`, {
      method: "POST",
      body: { description: trimmed },
    });
  };

  const cancelEditing = () => {
    setEditing(false);
    setDescription(note?.description || "");
    if (isNew) onCancelCreate?.();
  };

  const onDeleteClick = () => {
    if (isNew) {
      cancelEditing();
      return;
    }
    setShowDelete(true);
  };

  const remove = () => {
    setShowDelete(false);
    submit(
      `/actions/exercise-section/remove/${note._id}${query}&kind=workout-note`,
      { method: "POST", body: {} }
    );
  };

  return (
    <div className="workout-note-block" onClick={(e) => e.stopPropagation()}>
      {editing || isNew ? (
        <>
          <p className="workout-note__label">
            <SvgIcon
              src="/img/new-icons/clipboard-notes.svg"
              classNames="workout-note__icon"
            />
            {t("athlete-portal:exerciseEditor.workoutNoteLabel")}
          </p>
          {/* Edit mode: one full-width outlined textarea, with a compact Save
              and a square delete button at the bottom right, inside the box. */}
          <div className="workout-note workout-note--editable">
            <textarea
              className="workout-note__input"
              ref={textAreaRef}
              value={description}
              maxLength={MAX_WORKOUT_NOTE_LENGTH}
              placeholder={t(
                "athlete-portal:exerciseEditor.workoutNotePlaceholder"
              )}
              onChange={(e) => setDescription(e.target.value)}
              onKeyDown={(e) => e.key === "Escape" && cancelEditing()}
            />
            <div className="workout-note__actions">
              <button
                type="button"
                data-button-type="primary"
                className="workout-note__save"
                onClick={saveDescription}
                disabled={isNew && !description.trim()}
              >
                {t("common:save")}
              </button>
              <button
                type="button"
                data-button-type="secondary"
                className="workout-note__delete"
                onClick={onDeleteClick}
                title={t("athlete-portal:exerciseEditor.deleteWorkoutNote")}
              >
                <SvgIcon src="/img/new-icons/red-trash.svg" noFilterOnLight />
              </button>
            </div>
          </div>
        </>
      ) : collapsible ? (
        <div className="workout-note workout-note--readonly workout-note--collapsible">
          <button
            type="button"
            className="workout-note__summary"
            aria-expanded={expanded}
            onClick={() => setExpanded((prev) => !prev)}
          >
            <SvgIcon
              src="/img/svg/angle-right.svg"
              classNames={`workout-note__chevron${
                expanded ? " workout-note__chevron--open" : ""
              }`}
            />
            <span className="workout-note__summary-label">
              <SvgIcon
                src="/img/new-icons/clipboard-notes.svg"
                classNames="workout-note__icon"
              />
              {t("athlete-portal:exerciseEditor.workoutNoteTitle")}
            </span>
          </button>
          {expanded && (
            <p className="workout-note__text workout-note__text--clamped">
              {note.description}
            </p>
          )}
        </div>
      ) : (
        // Saved state: a card with the bold title inside, a "..." menu
        // (edit/delete) at the top right when editable, and a small muted body.
        <div
          className={`workout-note workout-note--readonly workout-note--card${
            editable ? " workout-note--clickable" : ""
          }`}
          onClick={editable ? () => setEditing(true) : undefined}
        >
          <div className="workout-note__header">
            <p className="workout-note__title">
              <SvgIcon
                src="/img/new-icons/clipboard-notes.svg"
                classNames="workout-note__icon"
              />
              {t("athlete-portal:exerciseEditor.workoutNoteTitle")}
            </p>
            {editable && (
              <div className="more workout-note__menu">
                <details role="list" open={menuOpened}>
                  <summary
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setMenuOpened(!menuOpened);
                    }}
                    onBlur={(e) => {
                      if (e.relatedTarget?.role === "listbox") return;
                      setMenuOpened(false);
                    }}
                    aria-haspopup="listbox"
                    aria-label={t(
                      "athlete-portal:exerciseEditor.workoutNoteOptions"
                    )}
                    className="p-0! h-auto!"
                  >
                    <SvgIcon src="/img/new-icons/vuesax-linear-add-square.svg" />
                  </summary>
                  <ul
                    role="listbox"
                    className={menuOpened ? "opened" : ""}
                    tabIndex="-1"
                  >
                    <li
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpened(false);
                        setEditing(true);
                      }}
                    >
                      <a>{t("common:edit")}</a>
                    </li>
                    <li
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpened(false);
                        setShowDelete(true);
                      }}
                    >
                      <a>
                        {t("athlete-portal:exerciseEditor.deleteWorkoutNote")}
                      </a>
                    </li>
                  </ul>
                </details>
              </div>
            )}
          </div>
          <p className="workout-note__text">{note.description}</p>
        </div>
      )}
      <ConfirmationModal
        isOpen={showDelete}
        icon="delete"
        title={t("athlete-portal:exerciseEditor.deleteWorkoutNoteTitle")}
        contentText={t(
          "athlete-portal:exerciseEditor.deleteWorkoutNoteConfirm"
        )}
        cancelButtonText={t("common:cancel")}
        cancelButtonAction={() => setShowDelete(false)}
        confirmButtonText={t("athlete-portal:exerciseEditor.deleteWorkoutNote")}
        confirmButtonAction={remove}
      />
    </div>
  );
};

export default WorkoutNoteBlock;
